import { useQuery } from '@tanstack/react-query';
import { QueryResult } from 'components/query-result/query-result';
import { listCreditNotesOptions } from 'generated/api-client/@tanstack/react-query.gen';
import {
  InvoiceFileButton,
  pennylaneKeyOf,
} from 'modules/billing/components/InvoiceFileButton';
import React, { FunctionComponent } from 'react';
import { Label, Message, Table } from 'semantic-ui-react';
import {
  formatAmount,
  formatCalendarDate,
  ON_DEMAND_QUERY_OPTIONS,
} from 'utils';

type Props = {
  companyId: number;
};

// The columns of the Zoho credit notes, with their download
export const CreditNotesTable: FunctionComponent<Props> = ({ companyId }) => {
  const query = useQuery(
    Object.assign(
      listCreditNotesOptions({ query: { companyId } }),
      ON_DEMAND_QUERY_OPTIONS,
    ),
  );

  return (
    <QueryResult query={query} label="avoirs">
      {({ creditNotes }): JSX.Element =>
        creditNotes.length === 0 ? (
          <Message>Il n&apos;y a pas d&apos;avoir</Message>
        ) : (
          <Table basic="very" compact selectable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Date</Table.HeaderCell>
                <Table.HeaderCell>N° avoir</Table.HeaderCell>
                <Table.HeaderCell>Montant</Table.HeaderCell>
                <Table.HeaderCell>Restant</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Facture(s) affectée(s)</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">
                  Télécharger
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {creditNotes.map((creditNote) => (
                <Table.Row
                  key={pennylaneKeyOf(
                    creditNote.billingIssuerId,
                    creditNote.creditNoteNumber,
                  )}
                >
                  <Table.Cell>
                    {formatCalendarDate(creditNote.issuedOn)}
                  </Table.Cell>
                  <Table.Cell>{creditNote.creditNoteNumber}</Table.Cell>
                  <Table.Cell>{formatAmount(creditNote.amount)}</Table.Cell>
                  <Table.Cell>
                    {formatAmount(creditNote.remainingAmountWithTax)} TTC
                  </Table.Cell>
                  <Table.Cell>
                    {creditNote.status === 'open' ? (
                      <Label color="blue">Ouvert</Label>
                    ) : (
                      <Label color="green">Soldé</Label>
                    )}
                  </Table.Cell>
                  <Table.Cell>{creditNote.creditedInvoiceNumber}</Table.Cell>
                  <Table.Cell textAlign="right">
                    <InvoiceFileButton
                      billingIssuerId={creditNote.billingIssuerId}
                      invoiceNumber={creditNote.creditNoteNumber}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )
      }
    </QueryResult>
  );
};
